import { HeartIcon } from '@/components/home/icons'
import { actionButtonClassName, PlayViewControls } from '@/components/play/PlayViewControls'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { selectIsCasinoFavorite, toggleCasinoFavorite } from '@/store/slices/casinoSlice'

export type CasinoPlayMode = 'demo' | 'real'

interface CasinoPlayActionBarProps {
	gameId: string
	playMode: CasinoPlayMode | null
	onModeChange: (mode: CasinoPlayMode) => void
	onFullscreen: () => void
	onTheaterToggle: () => void
	isTheaterMode: boolean
}

export function CasinoPlayActionBar({ gameId, playMode, onModeChange, onFullscreen, onTheaterToggle, isTheaterMode }: CasinoPlayActionBarProps) {
	const dispatch = useAppDispatch()
	const isFavorite = useAppSelector(selectIsCasinoFavorite(gameId))

	const handleFavoriteClick = () => {
		dispatch(toggleCasinoFavorite(gameId))
	}

	return (
		<div className='flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card px-3 py-2.5 sm:px-4'>
			<button
				type='button'
				onClick={handleFavoriteClick}
				className={cn(actionButtonClassName, isFavorite && 'border-primary/40 bg-primary/10 text-primary')}
				aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
				aria-pressed={isFavorite}
			>
				<HeartIcon className='h-4 w-4' />
			</button>

			<div className='flex flex-1 items-center justify-center'>
				<div className='flex items-center rounded-full border border-border bg-secondary p-1'>
					{(['demo', 'real'] as const).map((mode) => {
						const isActive = playMode === mode

						return (
							<button
								key={mode}
								type='button'
								onClick={() => onModeChange(mode)}
								className={cn(
									'relative rounded-full px-4 py-2 text-sm font-medium tracking-[-0.21px] transition-colors sm:px-6',
									isActive ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground',
								)}
							>
								{mode === 'demo' ? 'Demo mode' : 'Real mode'}
								{isActive ? <span className='absolute inset-x-3 -bottom-0.5 h-0.5 rounded-full bg-primary sm:inset-x-4' aria-hidden /> : null}
							</button>
						)
					})}
				</div>
			</div>

			<PlayViewControls onFullscreen={onFullscreen} onTheaterToggle={onTheaterToggle} isTheaterMode={isTheaterMode} />
		</div>
	)
}
